/* eslint-disable react/require-default-props */
import React from 'react';
import iconIA from 'assets/images/icone-ia-gradiente.svg';
import Loader from 'components/atoms/loader';
import { useSpeech } from 'hooks/useSpeech';
import { OverviewSection } from './types';
import SituacaoSection from './sections/SituacaoSection';
import ResumoSection from './sections/ResumoSection';
import AndamentosSection from './sections/AndamentosSection';
import ChatSection from './sections/ChatSection';
import AnnotationsSection from './sections/AnnotationsSection';
import { Container } from './styles';

interface OverviewControlProps {
  sections: OverviewSection[];
  loading?: boolean;
  inline?: boolean;
  onClose: () => void;
}

const OverviewControl = ({ sections, loading, inline = false, onClose }: OverviewControlProps) => {
  const { speak, stop, isSpeaking, isLoading } = useSpeech();

  const renderSection = (section: OverviewSection) => {
    switch (section.type) {
      case 'situacao':
        return <SituacaoSection data={section.data} />;

      case 'resumo':
        return <ResumoSection data={section.data} />;

      case 'andamentos':
        return <AndamentosSection data={section.data} />;

      case 'chat':
        return <ChatSection data={section.data} />;

      case 'anotacoes':
        return <AnnotationsSection data={section.data} />;

      default:
        return null;
    }
  };

  const buildOverviewText = () => {
    let text = '';

    sections.forEach((section) => {
      switch (section.type) {
        case 'situacao':
          if (section.data) {
            text += `Situação atual: ${section.data}. `;
          }
          break;

        case 'resumo':
          if (section.data) {
            text += `Resumo do chat: ${section.data}. `;
          }
          break;

        case 'andamentos':
          if (section.data?.length) {
            text += 'Andamentos: ';
            section.data.forEach((item: any) => {
              text += `${item.title}. `;
            });
          }
          break;

        case 'chat':
          if (section.data?.length) {
            text += 'Principais mensagens do chat: ';
            section.data.forEach((item: any) => {
              text += `${item.mensagem}. `;
            });
          }
          break;

        case 'anotacoes':
          if (section.data) {
            const { pendencias, pontos_atencao, decisoes } = section.data;

            if (pendencias?.length) {
              text += 'Pendências: ';
              pendencias.forEach((p: string) => {
                text += `${p}. `;
              });
            }

            if (pontos_atencao?.length) {
              text += 'Pontos de atenção: ';
              pontos_atencao.forEach((p: string) => {
                text += `${p}. `;
              });
            }

            if (decisoes?.length) {
              text += 'Decisões internas: ';
              decisoes.forEach((d: string) => {
                text += `${d}. `;
              });
            }
          }
          break;

        default:
          break;
      }
    });

    return text;
  };

  const handleSpeak = async () => {
    if (isSpeaking) {
      stop();
      return;
    }

    const text = buildOverviewText();
    if (text) {
      await speak(text);
    }
  };

  return (
    <Container inline={inline}>
      {!inline && (
        <button
          type="button"
          aria-label="Fechar visão geral"
          className="overview-backdrop"
          onClick={onClose}
        />
      )}
      <div className="overview-panel">
        <div className="overview-header d-flex justify-content-between align-items-start p-3">
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <img alt="Ícone IA" src={iconIA} height="25" />
              <strong className="ml-2">Visão Geral</strong>
            </div>

            <small className="text-muted">Resumo criado por IA. Verifique as informações.</small>
          </div>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary btn-speak mr-2"
              onClick={handleSpeak}
            >
              <span className="span-speak d-flex align-items-center">
                {' '}
                <i className="material-icons mr-1">{isSpeaking ? 'stop' : 'volume_up'}</i>
                {isLoading ? 'Carregando...' : isSpeaking ? 'Parar' : 'Ler em voz alta'}
              </span>
            </button>
            <button aria-label="Close" className="close" type="button" onClick={onClose}>
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <div className="overview-content">
            {sections
              .filter((section) => !section.hidden)
              .map((section) => {
                const key = `${section.type}-${JSON.stringify(section.data)?.length || 0}`;
                return <div key={key}>{renderSection(section)}</div>;
              })}
          </div>
        )}
      </div>
    </Container>
  );
};

export default OverviewControl;
